<template>
    <div class="py-4 flex flex-col gap-4">
        <div class="mt-2 px-4">
            <app-select
                name="video_group_select"
                v-model="state.selectedVideoGroupId"
            >
                <option
                    v-for="group in state.videoGroups"
                    :value="group.id"
                    :key="group.id"
                >
                    {{ group.name }}
                </option>
            </app-select>
        </div>
        <div
            v-for="video in state.videos"
            :key="video.id"
            class="px-4 py-2 cursor-move"
            @dragstart="emit('videodrag', $event, video, 'video')"
            @drop="emit('videodrop', $event, 'video')"
            @dblclick="handleClick(video)"
            draggable="true"
        >
            <div class="flex gap-2 items-center">
                <img :src="video.thumbnail" class="h-[40px]" />
                <div class="text-bold text-[15px]">{{ video.title }}</div>
            </div>
        </div>
        <div
            class="
                bg-primary
                rounded-lg
                mx-4
                py-2
                px-3
                text-white text-md
                flex
                items-center
                gap-1
                w-fit
            "
            @click="state.isOpen = true"
        >
            <ion-icon name="add-outline"></ion-icon>
            Upload video
        </div>
        <MiniModalCustom title="Upload video" :is-open="state.isOpen">
            <div
                v-if="state.video.isUploading"
                class="flex flex-col items-center justify-center"
            >
                <LoadingCircle></LoadingCircle>
                <div class="mt-3 text-[16px] text-bold">
                    {{ state.video.progress }}
                </div>
            </div>
            <form method="post" v-else>
                <div class="my-2">
                    <label for="title" class="block font-semibold my-2 text-sm">
                        Title
                    </label>
                    <new-input
                        required
                        maxlength="128"
                        label="Title"
                        name="title"
                        placeholder="Enter video title"
                        v-model="state.video.title"
                    ></new-input>
                </div>

                <div class="my-2">
                    <label
                        for="description"
                        class="block font-semibold my-2 text-sm"
                    >
                        Description
                    </label>
                    <new-input
                        maxlength="128"
                        label="Description"
                        name="description"
                        placeholder="Enter video description"
                        v-model="state.video.description"
                    ></new-input>
                </div>

                <video-input @change="handleVideoInput"></video-input>

                <div class="mt-8 flex items-center gap-4 justify-end">
                    <app-button
                        @click="state.isOpen = false"
                        class="border border-accent text-accent !bg-white"
                    >
                        Cancel
                    </app-button>
                    <app-button
                        :disabled="state.file === null"
                        type="button"
                        class="bg-primary text-white"
                        @click="uploadVideo()"
                        >Submit</app-button
                    >
                </div>
            </form>
        </MiniModalCustom>
    </div>
</template>

<script setup>
import { defineEmits, reactive, onMounted, defineProps, ref, watch } from "vue";

const emit = defineEmits(["updated", "videodrag", "videodrop", "dblclick"]);

const props = defineProps({
    videos: {
        type: Array,
    },
});

const state = reactive({
    isOpen: false,
    clickTimeoutId: null,
    selectedVideoGroupId: null,
    videoGroups: [],
    videos: [],
    video: {
        title: "",
        description: "",
        file: null,
        thumbnail: null,
        progress: "Please wait, uploading 0%",
        isUploading: false,
        uploadId: null,
        parts: [],
    },
});

watch(
    () => state.selectedVideoGroupId,
    (videoGroupId) => {
        getVideos(videoGroupId);
    }
);

const teamIdFromUrl = new URLSearchParams(window.location.search).get("teamId");

const inputFileRef = ref(null);

async function getVideoGroups() {
    const teamId = teamIdFromUrl ? teamIdFromUrl : "";
    const resp = await axios.get("/video/groups?teamId=" + teamId);

    if (resp.data.length && resp.data.length > 0) {
        state.videoGroups = resp.data;
        state.selectedVideoGroupId = resp.data[0].id;
        getVideos(state.selectedVideoGroupId);
    }
}

async function getVideos(videoGroupId) {
    const teamId = teamIdFromUrl ? teamIdFromUrl : "";
    const resp = await axios.get(
        `/video/list?teamId=${teamId}&videoGroupId=${videoGroupId}`
    );
    state.videos = resp.data;
}

function handleClick(video) {
    if (!state.clickTimeoutId) {
        state.clickTimeoutId = setTimeout(() => {}, 50);
    } else {
        clearTimeout(state.clickTimeoutId);
        emit("dblclick", video);
    }
}

onMounted(() => {
    getVideoGroups();
});

function handleVideoInput(metadata) {
    state.video.file = metadata.file;
    state.video.thumbnail = metadata.thumbnail;
}

async function uploadVideo() {
    try {
        state.video.isUploading = true;
        let dir = teamIdFromUrl;

        const key = state.video.file.name;
        const createResponse = await axios.post(
            "/upload/create-multipart-upload",
            { key, dir }
        );
        state.video.uploadId = createResponse.data.UploadId;

        await uploadParts(createResponse.data.Key);
        const resp = await completeUpload(createResponse.data.Key);
        await createVideoEntry(resp.data.url);
    } catch (e) {
        console.error(e);
    }

    state.video.isUploading = false;
    state.video.parts = [];
}

async function uploadParts(key) {
    const fileSize = state.video.file.size;
    let start = 0;
    let partNumber = 1;
    const partSize = 100 * 1024 * 1024; // 100MB part size
    const partLength = Math.round(state.video.file.size / partSize);
    let partIndex = 0;

    while (start < fileSize) {
        const end = Math.min(start + partSize, fileSize);
        const filePart = state.video.file.slice(start, end);

        // Get presigned URL from the server
        const presignedResponse = await axios.post(
            "/upload/generate-presigned-url",
            {
                key: key,
                uploadId: state.video.uploadId,
                partNumber,
            }
        );

        let presignedUrl = presignedResponse.data.url;
        presignedUrl = presignedUrl.replace(
            "http://host.docker.internal:4566",
            "http://localhost:4566"
        );

        // Upload the file part to S3
        const uploadResponse = await axios.put(presignedUrl, filePart, {
            headers: { "Content-Type": "application/octet-stream" },
        });

        state.video.parts.push({
            PartNumber: partNumber,
            ETag: uploadResponse.headers.etag,
        });

        start = end;
        partNumber++;
        partIndex++;
        const progress = Math.round((partIndex / partLength) * 100);
        if (progress < 99) {
            state.video.progress = `Please wait, uploading ${progress}%`;
        } else {
            state.video.progress = "Processing...";
        }
    }
}

async function completeUpload(key) {
    return axios.post("/upload/complete-multipart-upload", {
        key: key,
        uploadId: state.video.uploadId,
        parts: state.video.parts,
    });
}

async function createVideoEntry(url) {
    let formData = new FormData();
    formData.append("url", url);
    formData.append("size", state.video.file.size);
    formData.append("title", state.video.title);
    formData.append("thumbnail", state.video.thumbnail);
    formData.append("description", state.video.description);
    formData.append("video_group_id", state.selectedVideoGroupId);
    if (teamIdFromUrl) {
        formData.append("team_id", teamIdFromUrl);
    }

    try {
        await axios.post("/video/store", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        getVideos(state.selectedVideoGroupId);
        state.isOpen = false;
    } catch (e) {
        console.error(e);
    }
}
</script>
